import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@evolua/ui";
import { registerSW } from "virtual:pwa-register";
import "@evolua/ui/styles.css";
import { createAppQueryClient } from "./api/create-query-client.js";
import { App } from "./App";
import { initTelemetry } from "./telemetry/init-telemetry.js";
import { TelemetryErrorBoundary } from "./telemetry/TelemetryErrorBoundary.js";
import { startWebVitals } from "./telemetry/web-vitals.js";

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("Elemento #root não encontrado");
}
const root: HTMLElement = rootEl;

initTelemetry();
startWebVitals();

async function clearDevServiceWorkers(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
}

async function boot(): Promise<void> {
  if (import.meta.env.DEV) {
    await clearDevServiceWorkers();
  } else {
    registerSW({
      immediate: true,
      onRegisteredSW(_swUrl, registration) {
        // Descarta cache de API legado (NetworkFirst 5s) se ainda existir.
        if ("caches" in window) {
          void caches.delete("evolua-api");
        }
        // Garante checagem periódica de update (aba aberta por muito tempo).
        if (registration) {
          window.setInterval(
            () => {
              void registration.update();
            },
            60 * 60 * 1000,
          );
        }
      },
      onNeedRefresh() {
        void import("./pwa/SwUpdateBanner.js").then(({ bindSwUpdate }) => {
          bindSwUpdate(async () => {
            window.location.reload();
          });
        });
      },
    });
  }

  const queryClient = createAppQueryClient();
  createRoot(root).render(
    <StrictMode>
      <TelemetryErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <App />
          </ThemeProvider>
        </QueryClientProvider>
      </TelemetryErrorBoundary>
    </StrictMode>,
  );
}

void boot();
